import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import { Toolbar, AppBar, IconButton, Stack, Grid, Box, Tooltip, Chip } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { FC, useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ShepherdTourContext } from 'react-shepherd';
import { IJob, IMappingFormSettingsResponse } from 'wisestep-api';
import {
  WsColors,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
  WsButton,
  IConversationState,
  ICandidateAggregatedData,
  getDateUsingFormat,
  timeSince,
  getDateDifferenceInMonths,
} from 'wisestep-base';

import PulseIconText from '../../../components/PulseIconText';
import { ResumeDetails, StyledButton } from '../../../features/candidates/utils/CandidateHelper';
import useMappingFormSettings from '../../../hooks/useMappingFormSettings';
import { useConversationWidgetStore } from '../../../utils/useConversationWidgetStore';
import { dataDeleteCandidate } from '../../deleteRequests/deleteRequestPage/DataDeleteCandidateQuery';

import ConversationWidgetDrawer from './ConversationWidgetDrawer';
import SwitchResumes from './SwitchResumes';
import UpdateProfile from './UpdateProfile';
import WidgetJobManagement from './WidgetJobManagement';

interface IConversationWidgetHeader {
  job: IJob | null;
  state: IConversationState;
  changeResume: (url: string, name: string) => void;
  candidateResumes: ResumeDetails[];
  aggregation: ICandidateAggregatedData | null;
  changeView?: (ischange: boolean) => void;
}

const ConversationWidgetHeader: FC<IConversationWidgetHeader> = ({
  job,
  state,
  changeResume,
  candidateResumes,
  aggregation,
  changeView,
}) => {
  const redirect = useLocation();
  const navigate = useNavigate();
  const tour = useContext(ShepherdTourContext);
  const [updateSubmission, setUpdateSubmission] = useState(false);
  const [selectedJob, setSelectedJob] = useState<IJob | null>(job);
  const candidate = state.candidate;
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const view = useConversationWidgetStore((state) => state.view);
  const refreshPage = useConversationWidgetStore((state) => state.refreshSource);
  const setRefreshPage = useConversationWidgetStore((state) => state.setRefreshSource);
  const onWidgetClose = useConversationWidgetStore((state) => state.onWidgetClose);
  const onNavigation = useConversationWidgetStore((state) => state.onNavigation);
  const isNativeSearch = useConversationWidgetStore((state) => state.isNativeSearch);
  const setIsLatest = useConversationWidgetStore((state) => state.setIsLatest);
  const eraseData = useConversationWidgetStore((state) => state.eraseData);
  const isInDnc = state.candidate.isInDnc;
  const resumeUpdatedOn = useConversationWidgetStore((state) => state.resumeUpdatedOn);
  const fromPage = useConversationWidgetStore((state) => state.fromPage);
  const mappingFormSettingsResponse = useMappingFormSettings();
  const [mappingFormSettings, setMappingFormSetings] = useState<
    IMappingFormSettingsResponse | undefined
  >();
  const [isOriginal, setIsOriginal] = useState(true);
  const resumeUpdatedOnJobBoard = candidate.dateResumeLastUpdatedOnJobBoard
    ? getDateDifferenceInMonths(resumeUpdatedOn, candidate.dateResumeLastUpdatedOnJobBoard) >= 3
    : true;
  const showGetLatestCV = isNativeSearch ? resumeUpdatedOnJobBoard : false;

  const getResumeMessage = () => {
    // const resumeUpdatedDate = new Date(resumeUpdatedOn).getTime();
    if (candidateResumes.length > 0) {
      return resumeUpdatedOnJobBoard
        ? {
            message: 'Latest CV available',
            isLatest: false,
            color: WsColors.Yellow300,
            textColor: WsColors.YellowCompliment,
            resumeDownloadedOn: candidateResumes[0].resumeUpdatedOn,
          }
        : {
            message: ' CV is upto date',
            isLatest: true,
            color: WsColors.TheBlues100,
            textColor: WsColors.TheBlues500,
            resumeDownloadedOn: candidateResumes[0].resumeUpdatedOn,
          };
    }
    return {
      message: 'Latest CV available',
      isLatest: false,
      color: WsColors.Yellow300,
      textColor: WsColors.YellowCompliment,
    };
  };

  useEffect(() => {
    setSelectedJob(job);
  }, [job]);

  useEffect(() => {
    if (mappingFormSettingsResponse.fields)
      setMappingFormSetings(mappingFormSettingsResponse.fields);
  }, [mappingFormSettingsResponse.fields]);

  const onDataDeleteClick = async (candidateMappingEmail: string) => {
    const response = await dataDeleteCandidate(candidateMappingEmail);
    if (response) {
      window.location.reload();
    }
  };

  const closeWidget = () => {
    onWidgetClose();
    if (redirect.pathname === '/candidate-layout/mappings') {
      window.location.reload();
    }
    if (redirect.pathname.indexOf('/candidate-job-board-profile') > -1) {
      navigate('/home');
    } else if (refreshPage) {
      setRefreshPage(false);
      window.location.reload();
    }
  };

  return (
    <>
      <AppBar sx={{ position: 'relative', bgcolor: WsColors.White }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" aria-label="close">
            <CloseIcon onClick={closeWidget} />
          </IconButton>
          <Grid container xs={12} direction="row" alignItems="center">
            <Grid item xs={view === 'submission' || isInDnc ? (eraseData ? 6 : 8) : 6}>
              <Box m={0.5} display="flex" whiteSpace={'break-spaces'}>
                {view === 'submission' && (
                  <WsTypography variant={WsTypoVariants.BODY2} color={WsTypoColors.QUATERNARY}>
                    {` @${candidate?.mappedJobTitle} ( ${candidate?.jobCode} )`}
                  </WsTypography>
                )}

                {view !== 'submission' && !isInDnc && (
                  <WidgetJobManagement
                    selectedJob={selectedJob}
                    ss={isEmpty(selectedJob) ? 4 : 8}
                  />
                )}
                {isInDnc && eraseData && (
                  <Grid item xs={3.9}>
                    <StyledButton
                      variant="outlined"
                      size="large"
                      onClick={() => onDataDeleteClick(candidate.email)}
                    >
                      Erase the Personal Data for {candidate.email}
                    </StyledButton>
                  </Grid>
                )}
              </Box>
              <Box
                display="flex"
                whiteSpace={'break-spaces'}
                flexDirection={'row'}
                m={0.5}
                rowGap={0.5}
              >
                <WsTypography variant={WsTypoVariants.BODY1} bold={true}>
                  {candidate?.name ?? ''}{' '}
                </WsTypography>
                {candidateResumes && candidateResumes?.length > 0 && (
                  <Stack direction="row">
                    <SwitchResumes
                      candidateResumes={candidateResumes}
                      changeResume={changeResume}
                      candidate={candidate}
                    />
                    <WsButton
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        setIsOriginal(!isOriginal);
                        changeView && changeView(!isOriginal);
                      }}
                    >
                      {isOriginal ? 'Show Text' : 'Show original'}
                    </WsButton>
                  </Stack>
                )}
              </Box>
            </Grid>

            <Grid
              item
              container
              xs={view === 'submission' ? 4 : 6}
              direction="row"
              justifyContent="flex-end"
            >
              {isNativeSearch ? (
                <Box pt={1.5} pl={1}>
                  {resumeUpdatedOn && (
                    <>
                      {showGetLatestCV && (
                        <>
                          {candidate?.dateLastActiveOnJobBoard ? (
                            <PulseIconText
                              text={`Last Active ${timeSince(
                                candidate?.dateLastActiveOnJobBoard
                              )} on ${candidate.source}`}
                              hover={
                                candidate?.dateLastActiveOnJobBoard
                                  ? getDateUsingFormat(
                                      candidate?.dateLastActiveOnJobBoard ?? '',
                                      'D MMMM YYYY, HH:mm'
                                    )
                                  : ''
                              }
                            />
                          ) : (
                            candidate?.dateResumeLastUpdatedOnJobBoard && (
                              <PulseIconText
                                text={`Resume Updated ${timeSince(
                                  candidate?.dateResumeLastUpdatedOnJobBoard
                                )} on ${candidate.source}`}
                                hover={
                                  candidate?.dateResumeLastUpdatedOnJobBoard
                                    ? getDateUsingFormat(
                                        candidate?.dateResumeLastUpdatedOnJobBoard ?? '',
                                        'D MMMM YYYY, HH:mm'
                                      )
                                    : ''
                                }
                              />
                            )
                          )}
                        </>
                      )}
                      <PulseIconText
                        hover={
                          getResumeMessage()?.resumeDownloadedOn
                            ? getDateUsingFormat(
                                getResumeMessage().resumeDownloadedOn ?? '',
                                'D MMMM YYYY, HH:mm'
                              )
                            : ''
                        }
                        text={`last downloaded  ${timeSince(
                          getResumeMessage()?.resumeDownloadedOn ?? ''
                        )}`}
                      />
                      <Chip
                        sx={{
                          backgroundColor: WsColors.White,
                          color: getResumeMessage()?.textColor ?? '',
                          borderRadius: 2,
                          marginRight: 2,
                        }}
                        icon={<InfoIcon style={{ color: getResumeMessage()?.textColor }} />}
                        label={getResumeMessage()?.message}
                      />
                    </>
                  )}
                  {showGetLatestCV && (
                    <WsButton variant="outlined" onClick={() => setIsLatest(true)}>
                      Get Latest CV
                    </WsButton>
                  )}
                </Box>
              ) : fromPage && fromPage === 'closure' ? null : (
                <Grid
                  display="flex"
                  alignItems="center"
                  pl={1}
                  gap={1}
                  xs={10}
                  justifyContent="space-evenly"
                >
                  <Grid xs={8}>
                    {candidate?.dateLastActiveOnJobBoard && (
                      <PulseIconText
                        text={`Last Active ${timeSince(candidate.dateLastActiveOnJobBoard)} on ${
                          candidate.source
                        }`}
                        hover={
                          candidate.dateLastActiveOnJobBoard
                            ? getDateUsingFormat(
                                candidate.dateLastActiveOnJobBoard,
                                'D MMMM YYYY, HH:mm'
                              )
                            : ''
                        }
                      />
                    )}
                    {candidate?.dateResumeLastUpdatedOnJobBoard && (
                      <PulseIconText
                        text={`Resume Updated ${timeSince(
                          candidate.dateResumeLastUpdatedOnJobBoard
                        )} on ${candidate.source}`}
                        hover={
                          candidate.dateResumeLastUpdatedOnJobBoard
                            ? getDateUsingFormat(
                                candidate.dateResumeLastUpdatedOnJobBoard,
                                'D MMMM YYYY, HH:mm'
                              )
                            : ''
                        }
                      />
                    )}
                  </Grid>
                  <WsButton
                    variant="outlined"
                    startIcon={<KeyboardArrowLeftIcon />}
                    onClick={() => onNavigation('prev')}
                  >
                    Prev
                  </WsButton>

                  <WsButton
                    variant="outlined"
                    endIcon={<ChevronRightIcon />}
                    onClick={() => onNavigation('next')}
                  >
                    Next
                  </WsButton>
                </Grid>
              )}
              <Box sx={{ float: 'right', ml: 0.5 }}>
                <Tooltip title=" Tour Guide">
                  <IconButton onClick={() => tour?.start()}>
                    <InfoIcon style={{ color: WsColors.TheBlues500, fontSize: 30 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>
        </Toolbar>
      </AppBar>
      {candidate && (
        <>
          {view === 'submission' ? (
            <ConversationWidgetDrawer
              aggregation={aggregation}
              open={updateSubmission}
              updateMapping={false}
              submitCandidate={true}
              state={state}
              recruiterAction={''}
              selectedJob={null}
              closeDrawer={() => setUpdateSubmission(false)}
              submissionPurpose="update"
            />
          ) : (
            <UpdateProfile
              candidate={state.candidate}
              pipeline={state.messageAndInteractionPipeline}
              openUpdatePanel={openDrawer}
              job={job ?? undefined}
              closeUpdatePanel={() => setOpenDrawer(false)}
              mappingFormSettings={mappingFormSettings}
            />
          )}
        </>
      )}
    </>
  );
};

export default ConversationWidgetHeader;
